import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { productionApi } from '../services/api';
import { channelMoney } from '../utils/channelMoney';

type Listing = { id: string; productId: string; name: string; sku: string | null; price: string; currency?: string; status: string };
type PricingCountry = { code: string; name: string; market: string };
export function useShopifyCatalog() {
  const workspaceId = (() => { try { return JSON.parse(localStorage.getItem('manager_current_restaurant') || 'null')?.id || null; } catch { return null; } })();
  const [selectedCountry, setCountry] = useState<string | null>(null);
  const countries = useQuery({
    queryKey: ['shopify-pricing-countries', workspaceId],
    queryFn: async () => (await productionApi.getShopifyPricingCountries()).data.data as { connected: boolean; countries: PricingCountry[] },
    staleTime: 300_000, retry: 1,
  });
  const options = countries.data?.countries || [];
  const country = selectedCountry ?? (options.some(c => c.code === 'US') ? 'US' : '');
  const catalog = useQuery({
    queryKey: ['shopify-catalog', workspaceId, country],
    queryFn: async () => {
      const data = (await productionApi.getShopifyCatalog(country || undefined)).data.data as {
        connected: boolean; shopDomain?: string; currency?: string; storeCurrency?: string; country?: string | null; products: Listing[]; truncated?: boolean;
      };
      if (data.connected && country && data.country !== country) throw new Error('Selected country prices are not available yet.');
      return data;
    },
    enabled: !countries.isPending,
    staleTime: 120_000,
    retry: 1,
  });
  return { ...catalog, country, setCountry, countries, options };
}

export default function ShopifyCatalog({ query }: { query: ReturnType<typeof useShopifyCatalog> }) {
  const [status, setStatus] = useState('ACTIVE');
  const { data, isPending, isError, isFetching, refetch } = query;
  if (isPending) return <div className="card p-5 text-slate-500">Loading Shopify products…</div>;
  if (isError) return <div className="card p-5 text-amber-800">
    <p>Shopify products could not be loaded. Your catalog may still contain products.</p>
    <button className="btn-secondary mt-3" onClick={() => refetch()} disabled={isFetching}>Retry Shopify</button>
    {query.country && <button className="btn-secondary mt-3 ml-2" onClick={() => query.setCountry('')}>Show store base prices</button>}
  </div>;
  if (!data?.connected) return null;
  const products = data.products.filter(p => status === 'ALL' || p.status === status);
  return <section className="card p-0 overflow-hidden" aria-label="Shopify catalog">
    <div className="p-5 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold text-slate-900">Shopify products</h2>
          <p className="text-sm text-slate-500">{data.shopDomain} · {data.products.length} variants</p>
        </div>
        <button className="btn-secondary" onClick={() => refetch()} disabled={isFetching}>{isFetching ? 'Refreshing…' : 'Refresh products'}</button>
      </div>
      <p className="text-sm text-slate-500">{data.country
        ? `Shopify prices for ${query.options.find(c => c.code === data.country)?.name || data.country} in ${data.currency || 'the source currency'}.`
        : `Shopify store base prices in ${data.currency}.`}{' '}Country prices can differ from the store base currency ({data.storeCurrency || data.currency}). Historical orders keep their original currency and price.</p>
      {query.countries.isError && <p className="text-sm text-amber-800">The country list could not be refreshed. Prices below are labeled with their Shopify currency. <button className="underline" onClick={() => query.countries.refetch()}>Retry countries</button></p>}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-600">Price for
          <select aria-label="Shopify pricing country" className="input w-auto" value={query.country} onChange={e => query.setCountry(e.target.value)}>
            <option value="">Store base ({data.storeCurrency || data.currency})</option>
            {query.options.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </label>
        <select aria-label="Shopify product status" className="input w-auto" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="ACTIVE">Active ({data.products.filter(p => p.status === 'ACTIVE').length})</option>
          <option value="DRAFT">Draft ({data.products.filter(p => p.status === 'DRAFT').length})</option>
          <option value="ARCHIVED">Archived ({data.products.filter(p => p.status === 'ARCHIVED').length})</option>
          <option value="ALL">All ({data.products.length})</option>
        </select>
        <Link className="text-sm text-primary-600 font-medium" to="/production?tab=orders">View open Shopify orders →</Link>
      </div>
      {data.truncated && <p className="text-sm text-amber-700">Showing the first 2,000 variants. Additional products remain in Shopify.</p>}
    </div>
    {products.length ? <div className="overflow-x-auto"><table className="w-full text-sm">
      <thead className="text-xs uppercase text-slate-500 border-y border-slate-100"><tr>
        <th className="px-5 py-3 text-left font-medium">Product / variant</th>
        <th className="px-5 py-3 text-left font-medium">SKU</th>
        <th className="px-5 py-3 text-right font-medium">Shopify price {data.currency && `(${data.currency})`}</th>
        <th className="px-5 py-3 text-left font-medium">Status</th>
      </tr></thead>
      <tbody>{products.map(p => <tr key={p.id} className="border-b border-slate-50">
        <td className="px-5 py-3 font-medium text-slate-900">{p.name}</td>
        <td className="px-5 py-3 text-slate-600">{p.sku || 'No SKU in Shopify'}</td>
        <td className="px-5 py-3 text-right whitespace-nowrap">{channelMoney(Number(p.price), p.currency || data.currency)}</td>
        <td className="px-5 py-3 text-slate-500">{p.status.toLowerCase()}</td>
      </tr>)}</tbody>
    </table></div> : <p className="px-5 pb-5 text-sm text-slate-500">No {status === 'ALL' ? '' : status.toLowerCase() + ' '}Shopify products found.</p>}
    <p className="px-5 py-3 text-xs text-slate-500">Current Shopify catalog prices. Checkout tax, shipping and discounts may change the order total. Warehouse costs and wholesale price lists come from their own systems.</p>
  </section>;
}
